/**
 * Back Button Component
 * Handles navigation back functionality with support for:
 * - Browser history navigation
 * - Android hardware back button (via Capacitor)
 * - iOS swipe gestures (future: via Capacitor)
 * - Fallback navigation when no history exists
 * 
 * @author Varun Pandey
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { Capacitor } from '@capacitor/core';

interface BackButtonProps {
  /** Fallback route when no history exists (default: '/user-home') */
  fallbackRoute?: string;
  /** Custom onClick handler (optional) */
  onClick?: () => void;
  /** Additional CSS classes */
  className?: string;
  /** Aria label for accessibility */
  ariaLabel?: string;
}

/**
 * Hook to handle Android hardware back button
 * This will work seamlessly when Capacitor is integrated
 */
const useAndroidBackButton = (onBack: () => void) => {
  useEffect(() => {
    // Only run on native platforms
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    // Dynamically import App plugin to avoid errors in web builds
    const setupBackButton = async () => {
      try {
        const { App } = await import('@capacitor/app');
        
        // Listen for back button press
        const backButtonListener = await App.addListener('backButton', () => {
          onBack();
        });

        // Cleanup listener on unmount
        return () => {
          backButtonListener.remove();
        };
      } catch (error) {
        // App plugin not available (web build or not installed)
        // Silently fail - this is expected in web builds
        console.debug('App plugin not available (web build)');
      }
    };

    setupBackButton();
  }, [onBack]);
};

export const BackButton: React.FC<BackButtonProps> = ({
  fallbackRoute = '/user-home',
  onClick,
  className = 'p-2 rounded-lg hover:bg-[#2C2C2E] transition-colors',
  ariaLabel = 'Go back',
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [canGoBack, setCanGoBack] = useState(false);

  // Check if we can go back in history
  useEffect(() => {
    // Check if there's history to go back to
    // In browser, we can check window.history.length
    // For native apps, we'll track navigation state
    const hasHistory = window.history.length > 1;
    
    // Also check if we're not on a root route
    const isRootRoute = location.pathname === '/' || 
                       location.pathname === '/login' || 
                       location.pathname === '/signup';
    
    setCanGoBack(hasHistory && !isRootRoute);
  }, [location.pathname]);

  const handleBack = () => {
    // If custom onClick is provided, use it
    if (onClick) {
      onClick();
      return;
    }

    // Check if we can go back in history
    if (canGoBack) {
      navigate(-1);
    } else {
      // Fallback to default route
      navigate(fallbackRoute, { replace: true });
    }
  };

  // Handle Android hardware back button
  useAndroidBackButton(handleBack);

  return (
    <button
      onClick={handleBack}
      className={className}
      aria-label={ariaLabel}
    >
      <ChevronLeft className="h-5 w-5 text-white" />
    </button>
  );
};
